import * as React from 'react';
import Icon from 'antd/lib/icon';
import injectSheet from 'react-jss';
import Progress from 'antd/lib/progress';
import { withRouter } from 'react-router';
import { useEffect, useState } from 'react';
import { arrayToObject } from 'src/utils/utils';
import { StylesProps } from 'src/Library/Types';
import { useInterval } from 'src/hooks/useInterval';
import { fetchBulkRequests } from 'src/api/bulkRequest';
import { getBulkRequestRoute } from 'src/routing/utils';
import { clone, isValidArray } from 'src/Library/Helper';
import { BulkRequestPollResponse } from '../pages/BulkRequest/TypesInBulkRequest/BulkRequestData';
import { getUploadProgress, REQUEST_STATUS } from '../pages/BulkRequest/BulkRequestUtils';

const styles = (theme: any) => ({
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#064E96',
    borderRadius: `${theme.radius.md}px ${theme.radius.md}px 0 0`,
    color: theme.colors.bgWhite,
  },
  popupTitle: {
    fontSize: theme.fontSize.h3,
  },
  popupFiles: {
    borderBottom: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.md,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: theme.fontSize.body,
  },
  popupBar: {
    width: '68%',
    marginRight: theme.spacing.sm,
    '& .ant-progress-text': {
      display: 'none',
    },
  },
  popup: {
    position: 'fixed',
    bottom: '35px',
    left: '35px',
    width: 388,
    zIndex: theme.zIndex.modal,
  },
  popupContent: {
    fontSize: theme.fontSize.small,
    backgroundColor: theme.colors.bgSubtle,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: `0 0 ${theme.radius.md}px ${theme.radius.md}px`,
    boxShadow: '5px 5px 5px -5px rgba(138,138,138,0.25)',
  },
  viewAllUpload: {
    padding: theme.spacing.md,
    color: theme.colors.primary,
  },
  fileNameDiv: {
    width: '70%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    paddingRight: 10,
  },
  closeIcon: {
    background: theme.colors.error,
    borderRadius: '0 16px 16px 0',
    position: 'relative',
    height: 56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    fontSize: theme.fontSize.h3,
    top: 56,
    left: 388,
  },
});

interface BulkRequestPopupInterface extends StylesProps<ReturnType<typeof styles>> {
  reloadShipments: () => void;
  newRequest?: {
    id: number | string;
    status: REQUEST_STATUS;
  }[],
}

const BulkRequestPopup = (props: BulkRequestPopupInterface) => {
  const { classes, newRequest, reloadShipments } = props;

  const [requests, setRequests] = useState<BulkRequestPollResponse[]>([]);
  const [togglePopup, setTogglePopup] = useState(false);

  useEffect(() => {
    /** refresh when any request is completed */
    const isFetch = isValidArray(requests) &&
      requests?.every(request => request.pendingRows === 0);
    if (isFetch) {
      reloadShipments();
    }
  }, [requests]);

  useEffect(() => {
    if (newRequest) {
      getRequestObject(newRequest);
    }
  }, [newRequest]);

  useInterval(() => {
    const requestsToPoll = requests?.filter(row => row.status === REQUEST_STATUS.PENDING
      || row.status === REQUEST_STATUS.IN_PROGRESS);
    if (requestsToPoll?.length) {
      pollRequests(requestsToPoll);
    }
  }, 10000);

  const getRequestObject = (request) => {
    setRequests([...requests, request]);
    setTogglePopup(false);
  };

  const pollRequests = async (requestsToPoll: BulkRequestPollResponse[]) => {
    const response = await fetchBulkRequests({
      requestIdList: requestsToPoll.map(row => row.id),
    });
    if (response.isSuccess) {
      const requestPollResponse: BulkRequestPollResponse = response.data.data.data;
      const requestPollResponseMap = arrayToObject(requestPollResponse, 'id');
      const requestToPollMap = clone(arrayToObject(requests, 'id'));
      Object.keys(requestToPollMap).forEach((key) => {
        if (requestPollResponseMap[key]) {
          requestToPollMap[key] = {
            id: requestToPollMap[key].id,
            fileName: requestToPollMap[key].fileName,
            totalRows: requestPollResponseMap[key].totalRows,
            successRows: requestPollResponseMap[key].successRows,
            pendingRows: requestPollResponseMap[key].pendingRows,
            errors: requestPollResponseMap[key].errors,
            status: requestPollResponseMap[key].status,
          };
        }
      });
      setRequests(Object.keys(requestToPollMap).map(key => requestToPollMap[key]));
    }
  };

  const handleToggle = () => {
    setTogglePopup(!togglePopup);
  }

  const renderClose = () => {
    const doneUploading = requests.every(req => req.pendingRows === 0);

    if (!doneUploading) {
      return;
    }

    return (
      <div className={classes.closeIcon}>
        <Icon type="close" style={{ color: '#FFFFFF' }} onClick={() => setRequests([])}/>
      </div>
    )
  }

  const uploadPopUpHeader = () => {
    return (
      <div>
        {/* {renderClose()} */}
        <div className={classes.popupHeader}>
          <span className={classes.popupTitle}>Upload Progress</span>
          <div>
          {togglePopup ? <Icon type="up" onClick={() => handleToggle()} /> :
            <Icon type="down" onClick={() => handleToggle()} />}
            {<Icon type="close" style={{ marginLeft: 8 }} onClick={() => setRequests([])} />}
          </div>
        </div>
      </div>
    );
  };

  const renderRequests = () => {
    return (
      requests?.sort((a, b) => Number(b.id) - Number(a.id)).map((row) => {
        const percent = getUploadProgress(row);
        const successPercentage = percent.successPercentage;
        const errorPercentage = percent.errorPercentage;
        const totalPercent = percent.percent;
        return (<div key= {row.id} className={classes.popupFiles}>
          <div className={classes.fileNameDiv}>
            <Icon type="paper-clip" style={{ marginRight: '8px' }} />
            {row.fileName}
          </div>
          <div style={{ width: '30%', display: 'flex', alignItems: 'center' }}>
          { row.status !== REQUEST_STATUS.EMPTY_FILE && row.status !== REQUEST_STATUS.INVALID_FILE ?
            <Progress
              className={errorPercentage === 100 ? classes.popupBar : ''}
              showInfo={errorPercentage !== 100}
              strokeColor={errorPercentage ? {
                [`${Math.floor(successPercentage)}`]: '#52C419',
                [`${Math.floor(successPercentage)} + 1`]: 'red',
                [`${Math.ceil(successPercentage + errorPercentage)}`]: 'red',
              } : {
                '0%': '#52C419',
                [`${Math.ceil(successPercentage)}`]: '#52C419',
              }}
              percent={errorPercentage ? Math.ceil(successPercentage + errorPercentage) :
                Math.ceil(successPercentage)}
            /> :
            <Progress
              showInfo={false}
              className={classes.popupBar}
              strokeColor={{
                '0%': 'red',
                '100%': 'red',
              }}
              percent={100}
              /> }
            {(errorPercentage === 100 || row.status === REQUEST_STATUS.EMPTY_FILE ||
              row.status === REQUEST_STATUS.INVALID_FILE) &&
              <Icon type="close-circle" style={{ marginRight: '5px', color: '#F4222D' }}
              theme="filled" />}
          </div>
        </div>);
      })
    );
  };

  if (!requests?.length) {
    return <></>;
  }

  return (
    <div className={classes.popup}>
      {uploadPopUpHeader()}
      <div className={classes.popupContent}>
        {!togglePopup &&
          <div style={{ maxHeight: '108px', overflow: 'auto' }}>
            {renderRequests()}
          </div>}
          <div className={classes.viewAllUpload}>
            <a href={getBulkRequestRoute()}
              target="_blank">
              View all files</a>
          </div>
      </div>
    </div>
  );
};

export default withRouter(injectSheet(styles)(BulkRequestPopup));